
import { Children, isValidElement, type ReactNode, type HTMLAttributes } from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// ============================================================================
// EntityCard Props (following WorkflowCard compound component pattern)
// ============================================================================

export interface EntityCardProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    /** Whether the card is selected */
    selected?: boolean;
    /** Accent color for left border (e.g., 'border-l-blue-500') */
    accentColor?: string;
    /** Icon background color class (e.g., 'bg-blue-100') */
    iconBgColor?: string;
    /** Icon text/stroke color class (e.g., 'text-blue-600') */
    iconColor?: string;
}

// Card dimensions config for consistent sizing (compact, flexible width)
export const entityCardConfig = {
    minWidth: 265,
    maxWidth: 322,
    height: 128,
    className: 'w-full min-h-32',
};

// ============================================================================
// Helper to extract child components by type
// ============================================================================

function extractChild(children: ReactNode, component: React.ComponentType<CardSlotProps>): ReactNode[] {
    return Children.toArray(children).filter(
        (child) => isValidElement(child) && child.type === component
    );
}

// ============================================================================
// Sub-components (compound component pattern)
// ============================================================================

interface CardSlotProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    className?: string;
}

export const EntityCardIcon = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn(className)} {...props}>
        {children}
    </div>
);

export const EntityCardTitle = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('text-left text-sm font-bold text-slate-800 leading-tight', className)} {...props}>
        {children}
    </div>
);

export const EntityCardSubtitle = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('text-left text-[10px] text-slate-500', className)} {...props}>
        {children}
    </div>
);

export const EntityCardDescription = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('mt-3 line-clamp-2 text-left text-[10px] text-slate-600 leading-tight', className)} {...props}>
        {children}
    </div>
);

export const EntityCardBadge = ({ children, className, ...props }: CardSlotProps) => (
    <span
        className={cn(
            'rounded-full px-1.5 py-0.5 text-[9px] font-medium',
            'bg-blue-100 text-blue-700',
            className
        )}
        {...props}
    >
        {children}
    </span>
);

export const EntityCardTopRight = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute right-2 top-2 z-10', className)} {...props}>
        {children}
    </div>
);

export const EntityCardBottomLeft = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute bottom-2 left-2 z-10', className)} {...props}>
        {children}
    </div>
);

export const EntityCardBottomRight = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('absolute bottom-2 right-2 z-10', className)} {...props}>
        {children}
    </div>
);

export const EntityCardFooter = ({ children, className, ...props }: CardSlotProps) => (
    <div
        className={cn(
            'mt-auto flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-[10px] text-slate-500',
            className
        )}
        {...props}
    >
        {children}
    </div>
);

export const EntityCardMeta = ({ children, className, ...props }: CardSlotProps) => (
    <div className={cn('flex items-center gap-3 text-[10px] text-slate-500', className)} {...props}>
        {children}
    </div>
);

// ============================================================================
// EntityCard Component (following WorkflowCard pattern)
// ============================================================================

export function EntityCard({
    children,
    className,
    selected = false,
    accentColor = 'border-l-blue-500',
    iconBgColor = 'bg-slate-100',
    iconColor = 'text-slate-600',
    ...props
}: EntityCardProps) {
    // Extract positioned children
    const icon = extractChild(children, EntityCardIcon);
    const title = extractChild(children, EntityCardTitle);
    const subtitle = extractChild(children, EntityCardSubtitle);
    const description = extractChild(children, EntityCardDescription);
    const badge = extractChild(children, EntityCardBadge);
    const topRight = extractChild(children, EntityCardTopRight);
    const bottomLeft = extractChild(children, EntityCardBottomLeft);
    const bottomRight = extractChild(children, EntityCardBottomRight);
    const footer = extractChild(children, EntityCardFooter);
    const meta = extractChild(children, EntityCardMeta);

    return (
        <Card
            className={cn(
                entityCardConfig.className,
                'relative border border-slate-200 p-3',
                'border-l-4',
                accentColor,
                'text-foreground/90 transition-all duration-200',
                'hover:cursor-pointer hover:border-slate-300 hover:bg-slate-50',
                // Shadow glow on hover
                'shadow-sm hover:shadow-md hover:shadow-blue-100/50',
                // Selection state
                selected && 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-200',
                className
            )}
            {...props}
        >
            {/* Positioned slots */}
            {topRight}

            {/* Header: Icon + Title/Subtitle/Description/Badge */}
            <div className="flex flex-row items-start gap-2">
                {/* Icon with entity color */}
                {icon.length > 0 && (
                    <div className={cn('flex shrink-0 items-center justify-center rounded-md p-1.5', iconBgColor, iconColor)}>
                        {icon}
                    </div>
                )}
                <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-1.5">
                        {title}
                        {badge}
                    </div>
                    {subtitle}
                    {/* Description moved up into header area */}
                    {description}
                </div>
            </div>

            {/* Meta info */}
            {meta}

            {/* Footer */}
            {footer}

            {/* Positioned slots */}
            {bottomLeft}
            {bottomRight}
        </Card>
    );
}

// Attach sub-components for compound pattern
EntityCard.Icon = EntityCardIcon;
EntityCard.Title = EntityCardTitle;
EntityCard.Subtitle = EntityCardSubtitle;
EntityCard.Description = EntityCardDescription;
EntityCard.Badge = EntityCardBadge;
EntityCard.TopRight = EntityCardTopRight;
EntityCard.BottomLeft = EntityCardBottomLeft;
EntityCard.BottomRight = EntityCardBottomRight;
EntityCard.Footer = EntityCardFooter;
EntityCard.Meta = EntityCardMeta;

